import { FormattedMessage } from "react-intl";

import LegalDocumentTracker from "@components/LegalDocumentTracker";
import MedicationTracker from "@components/MedicationTracker";
import ProviderTracker from "@components/ProviderTracker";
import ButtonLink from "@components/typography/ButtonLink";
import InternalLink from "@components/typography/InternalLink";
import useMyExpert from "@hooks/useMyExpert";
import useOpener, { Opener } from "@hooks/useOpener";
import {
  CarePlanActionItemFragment as CarePlanActionItem,
  CarePlanActionItemCtaEnum,
  GetJourneyWithPlanQuery,
  TrackerStatusEnum
} from "@typing/Generated";
import { MedicationGroups, ProviderGroups } from "@typing/Models";
import { journeyRoute } from "@utils/routeUtils";

type ActionItemCtaProps = {
  currentStage: TrackerStatusEnum;
  journeyId: string;
  opener: Opener | null;
  routerLinkAfterJourney: string;
  type: CarePlanActionItemCtaEnum;
};

const ActionItemCta = ({ currentStage, journeyId, opener, routerLinkAfterJourney, type }: ActionItemCtaProps) => {
  const myExpert = useMyExpert();
  return (
    <>
      {currentStage === TrackerStatusEnum.COMPLETED ? (
        <InternalLink to={journeyRoute({ journeyId, linkAfterJourney: routerLinkAfterJourney, myExpert })}>
          <FormattedMessage id={`pages.carePlanActionItem.view.${type}`} />
        </InternalLink>
      ) : opener ? (
        <ButtonLink data-test={type} onClick={opener.open}>
          {currentStage === TrackerStatusEnum.NOT_STARTED ? (
            <FormattedMessage id={`pages.carePlanActionItem.begin.${type}`} />
          ) : (
            <FormattedMessage id={`pages.carePlanActionItem.continue.${type}`} />
          )}
        </ButtonLink>
      ) : null}
    </>
  );
};

type Props = {
  actionItem: CarePlanActionItem;
  batchedMedications: MedicationGroups;
  batchedProviders: ProviderGroups;
  journey: GetJourneyWithPlanQuery["journey"];
};

const ActionItemCtas = ({ actionItem, batchedMedications, batchedProviders, journey }: Props) => {
  const medicationTrackerOpener = useOpener();
  const providerTrackerOpener = useOpener();
  const legalDocumentTrackerOpener = useOpener();

  return (
    <>
      {actionItem.cta === CarePlanActionItemCtaEnum.complete_consent_form && (
        <ActionItemCta
          currentStage={TrackerStatusEnum.COMPLETED}
          journeyId={journey.id}
          opener={null}
          routerLinkAfterJourney="/care-plan#tool-consent-form"
          type={CarePlanActionItemCtaEnum.complete_consent_form}
        />
      )}
      {actionItem.cta === CarePlanActionItemCtaEnum.complete_legal_document_tracker && (
        <>
          <ActionItemCta
            currentStage={journey.legalTrackerStatus}
            journeyId={journey.id}
            opener={legalDocumentTrackerOpener}
            routerLinkAfterJourney="/care-plan#tool-legal"
            type={CarePlanActionItemCtaEnum.complete_legal_document_tracker}
          />
          <LegalDocumentTracker journey={journey} legalDocumentsOpener={legalDocumentTrackerOpener} />
        </>
      )}
      {actionItem.cta === CarePlanActionItemCtaEnum.complete_medication_tracker && (
        <>
          <ActionItemCta
            currentStage={journey.medicationTrackerStatus}
            journeyId={journey.id}
            opener={medicationTrackerOpener}
            routerLinkAfterJourney="/care-profile/medical#medication"
            type={CarePlanActionItemCtaEnum.complete_medication_tracker}
          />
          <MedicationTracker
            batchedMedications={batchedMedications}
            journey={journey}
            medicationTrackerOpener={medicationTrackerOpener}
          />
        </>
      )}
      {actionItem.cta === CarePlanActionItemCtaEnum.complete_provider_list && (
        <>
          <ActionItemCta
            currentStage={journey.providerTrackerStatus}
            journeyId={journey.id}
            opener={providerTrackerOpener}
            routerLinkAfterJourney="/care-profile/medical#provider"
            type={CarePlanActionItemCtaEnum.complete_provider_list}
          />
          <ProviderTracker
            batchedProviders={batchedProviders}
            journey={journey}
            providerTrackerOpener={providerTrackerOpener}
          />
        </>
      )}
    </>
  );
};

export default ActionItemCtas;
